import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Send } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from '@/hooks/use-mobile';
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase.js';
import { reportContactConversion } from '@/lib/gtag';

const MAX_NAME_LENGTH = 50;
const MAX_PHONE_LENGTH = 20;
const MAX_EMAIL_LENGTH = 100;
const MAX_COMPANY_LENGTH = 100;
const MAX_MESSAGE_LENGTH = 500;

const ContactForm = ({ isVisible = true }) => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showMessageCounter, setShowMessageCounter] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const formValidatedRef = useRef(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formState.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formState.name.length > MAX_NAME_LENGTH) {
      newErrors.name = `Name cannot exceed ${MAX_NAME_LENGTH} characters`;
    }
    
    if (!formState.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (formState.phone.length > MAX_PHONE_LENGTH) {
      newErrors.phone = `Phone number cannot exceed ${MAX_PHONE_LENGTH} characters`;
    }
    
    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formState.email.length > MAX_EMAIL_LENGTH) {
      newErrors.email = `Email cannot exceed ${MAX_EMAIL_LENGTH} characters`;
    }
    
    if (!formState.company.trim()) {
      newErrors.company = 'Company information is required';
    } else if (formState.company.length > MAX_COMPANY_LENGTH) {
      newErrors.company = `Company information cannot exceed ${MAX_COMPANY_LENGTH} characters`;
    }
    
    if (formState.message.length > MAX_MESSAGE_LENGTH) {
      newErrors.message = `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`;
    }
    
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
    
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
    
    if (formValidatedRef.current || touched[name]) {
      const newErrors = validateForm();
      setErrors(newErrors);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    if (name === 'message') {
      setShowMessageCounter(false);
    }
    
    const newErrors = validateForm();
    setErrors(newErrors);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.name === 'message') {
      setShowMessageCounter(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setTouched({
      name: true,
      email: true,
      phone: true,
      company: true,
      message: true
    });
    
    formValidatedRef.current = true;
    
    const newErrors = validateForm();
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      
      try {
        await addDoc(collection(db, "contactSubmissions"), {
          name: formState.name,
          email: formState.email,
          phone: formState.phone,
          company: formState.company,
          message: formState.message,
          createdAt: serverTimestamp(),
          status: 'new'
        });

        reportContactConversion();
        
        setFormState({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        
        setTouched({});
        setErrors({});
        setShowMessageCounter(false);
        formValidatedRef.current = false;
        
        toast({
          title: "Message Sent",
          description: "We'll get back to you as soon as possible.",
          duration: 5000,
        });
      } catch (error) {
        console.error("Error submitting contact form: ", error);
        
        toast({
          title: "Error",
          description: "There was a problem sending your message. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const isFormValid = () => {
    const validationErrors = validateForm();
    return Object.keys(validationErrors).length === 0 && !isSubmitting;
  };

  return (
    <div 
      className={cn(
        "transition-all duration-700 delay-500",
        isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
      )}
    >
      <form
        id="contact-form"
        onSubmit={handleSubmit}
        className="glass-card rounded-2xl p-4 sm:p-6 md:p-8 transition-shadow duration-500"
      >
        <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Send Us a Message</h3>
        
        <div className="space-y-4 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Full Name*</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formState.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={cn(
                "w-full px-3 py-2 text-sm sm:text-base rounded-lg border bg-white/80 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all",
                touched.name && errors.name ? "border-destructive" : ""
              )}
              placeholder="Your full name"
              maxLength={MAX_NAME_LENGTH}
            />
            {touched.name && errors.name && (
              <p className="text-xs sm:text-sm text-destructive mt-1">{errors.name}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium">Phone Number*</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={formState.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={cn(
                "w-full px-3 py-2 text-sm sm:text-base rounded-lg border bg-white/80 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all",
                touched.phone && errors.phone ? "border-destructive" : ""
              )}
              placeholder="Your phone number"
              maxLength={MAX_PHONE_LENGTH}
            />
            {touched.phone && errors.phone && (
              <p className="text-xs sm:text-sm text-destructive mt-1">{errors.phone}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email*</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formState.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={cn(
                "w-full px-3 py-2 text-sm sm:text-base rounded-lg border bg-white/80 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all",
                touched.email && errors.email ? "border-destructive" : ""
              )}
              placeholder="your@email.com"
              maxLength={MAX_EMAIL_LENGTH}
            />
            {touched.email && errors.email && (
              <p className="text-xs sm:text-sm text-destructive mt-1">{errors.email}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="company" className="text-sm font-medium">Company Website or Company Name*</Label>
            <Input
              type="text"
              id="company"
              name="company"
              value={formState.company}
              onChange={handleChange}
              onBlur={handleBlur}
              className={cn(
                "w-full px-3 py-2 text-sm sm:text-base rounded-lg border bg-white/80 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all",
                touched.company && errors.company ? "border-destructive" : ""
              )}
              placeholder="Your company name or website"
              maxLength={MAX_COMPANY_LENGTH}
            />
            {touched.company && errors.company && (
              <p className="text-xs sm:text-sm text-destructive mt-1">{errors.company}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="message" className="text-sm font-medium">Message</Label>
              {showMessageCounter && (
                <span className="text-xs text-muted-foreground">
                  {formState.message.length}/{MAX_MESSAGE_LENGTH}
                </span>
              )}
            </div>
            <Textarea
              id="message"
              name="message"
              value={formState.message}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              rows={isMobile ? 3 : 4}
              className={cn(
                "w-full px-3 py-2 text-sm sm:text-base rounded-lg border bg-white/80 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary transition-all",
                touched.message && errors.message ? "border-destructive" : ""
              )}
              placeholder="How can we help you?"
              maxLength={MAX_MESSAGE_LENGTH}
            />
            {touched.message && errors.message && (
              <p className="text-xs sm:text-sm text-destructive mt-1">{errors.message}</p>
            )}
          </div>
        </div>
        
        <button
          type="submit"
          disabled={!isFormValid()}
          className={cn(
            "w-full mt-6 py-2 sm:py-3 px-4 rounded-lg text-sm sm:text-base font-medium transition-all",
            "bg-primary text-white hover:bg-primary/90",
            "flex items-center justify-center",
            (!isFormValid() || isSubmitting) ? "opacity-70 cursor-not-allowed" : ""
          )}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              <Send size={16} className="mr-2" />
              Send Message
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;

